import { useCallback, useEffect, useRef, useState } from 'react'
import { Check, Loader2, Mail, Pencil, Send, Trash2 } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { ApiClientError, apiClient } from '@/lib/api/client'
import type { HelmReplyDraft } from '@/lib/api/types'
import { cn } from '@/lib/utils'

const POLL_MS = 2_000
const POLL_LIMIT_MS = 120_000

const CHANNEL: Record<string, string> = { EMAIL: 'Email', WHATSAPP: 'WhatsApp' }

type Phase = { kind: 'drafting' } | { kind: 'failed'; message: string } | { kind: 'ready' }

function findingsFrom(error: unknown): string[] | null {
  if (!(error instanceof ApiClientError) || error.code !== 'REPLY_DRAFT_HAS_FINDINGS') return null
  const details = error.details as { findings?: unknown } | undefined
  return Array.isArray(details?.findings) ? details.findings.map(String) : [error.message]
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

interface Props {
  /** The background job writing the draft. */
  jobId: string
  inquiryId: string | null
  /** A person approved and sent it, or discarded it; in a call, Beacon hears which. */
  onDecided?: (summary: string, sent: boolean, failure?: string | null) => void
  /** Whether it waits on the person now: drafted and neither sent nor discarded. */
  onAwaitingChange?: (awaiting: boolean) => void
}

/**
 * A reply to a client that Beacon drafted, waiting on a person.
 *
 * Nothing is sent when this appears, or ever by Beacon: only Approve & send
 * here (or Send on the inquiry page) sends it, through the same route, the
 * same admin check and the same supplier-and-cost check as any reply draft.
 * The draft can be edited first; discarding it sends nothing.
 */
export function ReplyApprovalCard({ jobId, inquiryId, onDecided, onAwaitingChange }: Props) {
  const [phase, setPhase] = useState<Phase>({ kind: 'drafting' })
  const [draft, setDraft] = useState<HelmReplyDraft | null>(null)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [editing, setEditing] = useState(false)
  const [findings, setFindings] = useState<string[]>([])
  const [busy, setBusy] = useState<'send' | 'discard' | null>(null)
  const [error, setError] = useState<string | null>(null)

  const show = useCallback((next: HelmReplyDraft) => {
    setDraft(next)
    setSubject(next.subject ?? '')
    setBody(next.body)
    setFindings(next.findings)
    setPhase({ kind: 'ready' })
  }, [])

  // Drafting runs in the background; follow the job until the draft exists.
  useEffect(() => {
    let cancelled = false
    const started = Date.now()
    const follow = async () => {
      try {
        while (!cancelled) {
          const job = await apiClient.getJob(jobId)
          if (job.status === 'SUCCEEDED') {
            const draftId = job.result?.draft_id
            if (typeof draftId !== 'string') throw new Error('The draft could not be found')
            const found = await apiClient.getReplyDraft(draftId)
            if (!cancelled) show(found)
            return
          }
          if (job.status === 'FAILED' || job.status === 'CANCELLED') {
            throw new Error(job.last_error || 'The draft could not be written')
          }
          if (Date.now() - started > POLL_LIMIT_MS) {
            throw new Error(
              'Drafting is taking longer than usual. It will appear under Reply drafts on the inquiry.',
            )
          }
          await wait(POLL_MS)
        }
      } catch (caught) {
        if (!cancelled) {
          setPhase({
            kind: 'failed',
            message: caught instanceof Error ? caught.message : 'The draft could not be written',
          })
        }
      }
    }
    void follow()
    return () => {
      cancelled = true
    }
  }, [jobId, show])

  const recipient = draft?.recipient ?? null
  const summary = `the reply to ${recipient ?? 'the client'}`
  const open = draft?.status === 'DRAFT'
  const edited = draft ? subject !== (draft.subject ?? '') || body !== draft.body : false
  const awaiting = phase.kind === 'ready' && open
  const reportAwaiting = useRef(onAwaitingChange)
  useEffect(() => {
    reportAwaiting.current = onAwaitingChange
  })
  useEffect(() => {
    reportAwaiting.current?.(awaiting)
  }, [awaiting])
  useEffect(() => () => reportAwaiting.current?.(false), [])

  async function approve() {
    if (!draft) return
    setBusy('send')
    setError(null)
    try {
      await apiClient.sendReplyDraft(draft.id, {
        subject: subject.trim() || null,
        body: body.trim(),
      })
      setDraft({ ...draft, status: 'SENT', subject: subject.trim() || null, body: body.trim() })
      setEditing(false)
      onDecided?.(summary, true)
    } catch (caught) {
      const found = findingsFrom(caught)
      if (found) {
        setFindings(found)
        setEditing(true)
      } else if (caught instanceof ApiClientError && caught.status === 409) {
        // Sent or replaced by someone else meanwhile: show what it is now.
        show(await apiClient.getReplyDraft(draft.id).catch(() => draft))
        setError(caught.message)
      } else {
        setError(caught instanceof Error ? caught.message : 'The reply was not sent')
      }
    } finally {
      setBusy(null)
    }
  }

  async function discard() {
    if (!draft) return
    setBusy('discard')
    setError(null)
    try {
      show(await apiClient.discardReplyDraft(draft.id))
      setEditing(false)
      onDecided?.(summary, false)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The draft was not discarded')
    } finally {
      setBusy(null)
    }
  }

  return (
    <section
      aria-label="Reply to the client"
      data-testid="reply-approval"
      data-awaiting-approval={awaiting ? '' : undefined}
      tabIndex={-1}
      className={cn(
        'min-w-0 scroll-mt-3 rounded-xl border bg-card p-4 text-sm outline-none',
        open && 'border-waiting/50',
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Mail
          aria-hidden
          className={cn('size-4 shrink-0', open ? 'text-waiting' : 'text-muted-foreground')}
        />
        <span className="font-medium">Reply to the client</span>
        <Badge variant="outline" className="text-xs">
          {CHANNEL[draft?.channel ?? 'EMAIL'] ?? draft?.channel}
        </Badge>
        {open && (
          <Badge variant="accent-soft" className="text-xs">
            Leaves Helm
          </Badge>
        )}
      </div>

      {phase.kind === 'drafting' && (
        <p className="mt-3 flex items-center gap-2 text-muted-foreground" role="status">
          <Loader2 aria-hidden className="size-4 animate-spin" />
          Drafting the reply…
        </p>
      )}

      {phase.kind === 'failed' && (
        <p className="mt-3 text-destructive" role="alert">
          {phase.message}
        </p>
      )}

      {phase.kind === 'ready' && draft && (
        <>
          <dl className="mt-3 grid grid-cols-[4.5rem_minmax(0,1fr)] gap-x-3 gap-y-1">
            <dt className="text-muted-foreground">To</dt>
            <dd className="break-words">{recipient ?? 'No email address on file'}</dd>
            {!editing && (
              <>
                <dt className="text-muted-foreground">Subject</dt>
                <dd className="break-words">{draft.subject || '(no subject)'}</dd>
              </>
            )}
          </dl>

          {draft.replying_to && (
            <blockquote className="mt-3 rounded-md bg-muted px-3 py-2 text-xs">
              <span className="text-muted-foreground">
                {draft.replying_to.from ?? 'The client'} wrote:
              </span>{' '}
              <span className="line-clamp-3 whitespace-pre-wrap">{draft.replying_to.excerpt}</span>
            </blockquote>
          )}

          {open && findings.length > 0 && (
            <Alert variant="destructive" className="mt-3">
              <AlertTitle>Edit these out before sending</AlertTitle>
              <AlertDescription>
                <ul className="list-disc pl-4">
                  {findings.map((finding) => (
                    <li key={finding}>{finding}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {open && draft.needs_from_team.length > 0 && (
            <Alert className="mt-3">
              <AlertTitle>The draft could not answer</AlertTitle>
              <AlertDescription>
                <ul className="list-disc pl-4">
                  {draft.needs_from_team.map((need) => (
                    <li key={need}>{need}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {editing && open ? (
            <div className="mt-3 space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor={`reply-subject-${draft.id}`}>Subject</Label>
                <Input
                  id={`reply-subject-${draft.id}`}
                  value={subject}
                  onChange={(event) => setSubject(event.target.value)}
                  disabled={busy !== null}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`reply-body-${draft.id}`}>Message</Label>
                <Textarea
                  id={`reply-body-${draft.id}`}
                  value={body}
                  onChange={(event) => setBody(event.target.value)}
                  disabled={busy !== null}
                  rows={8}
                />
              </div>
            </div>
          ) : (
            <p
              aria-label="Message"
              className="mt-3 max-h-64 overflow-y-auto whitespace-pre-wrap break-words rounded-md border border-border/60 p-3"
            >
              {draft.body}
            </p>
          )}

          {error && <p className="mt-3 text-destructive">{error}</p>}

          {open ? (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                onClick={() => void approve()}
                disabled={busy !== null || !recipient || !body.trim()}
              >
                {busy === 'send' ? <Loader2 className="animate-spin" /> : <Send />}
                {edited ? 'Approve & send edited' : 'Approve & send'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditing((value) => !value)}
                disabled={busy !== null}
                aria-pressed={editing}
              >
                {editing ? <Check /> : <Pencil />}
                {editing ? 'Done editing' : 'Edit'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => void discard()}
                disabled={busy !== null}
              >
                {busy === 'discard' ? <Loader2 className="animate-spin" /> : <Trash2 />}
                Discard
              </Button>
              <p className="w-full text-xs text-muted-foreground">
                Nothing is sent until you approve. It goes from Helm into the client&apos;s email
                thread.
              </p>
            </div>
          ) : (
            <p className="mt-3 text-muted-foreground" role="status">
              {draft.status === 'SENT'
                ? `Sent to ${recipient ?? 'the client'}.`
                : draft.status === 'DISCARDED'
                  ? 'Discarded. Nothing was sent.'
                  : 'Replaced by a newer draft.'}
              {draft.status === 'SUPERSEDED' && inquiryId && (
                <>
                  {' '}
                  <a className="underline" href={`/inquiries/${inquiryId}`}>
                    See the inquiry
                  </a>
                </>
              )}
            </p>
          )}
        </>
      )}
    </section>
  )
}
